import {type CrmStateRedirectOptions, useCrmStateStore, UserState} from '~/stores/crmStateStore';
import {OidcService} from '~/openapi';

enum Ceremony {
	SIGN_IN,
	UPDATE_PROFILE,
	LOGOUT,
}

enum CrmScope {
	WEB = 'web',
	WEB_LIST_LOGIN = 'webListLogin',
	WEB_STANDARD_LOGIN = 'webStandardLogin',
	WEB_PRODUCTS_LOGIN = 'webProductsLogin',
	WEB_REGISTER = 'webRegister',
	WEB_RESET_PASSWORD = 'webResetPassword',
	WEB_LOGIN_AFTER_PASSWORD_RESET = 'webLoginAfterPasswordReset',
	WEB_REPEAT_LOGIN = 'webRepeatLogin',
}

const stateToCeremonyMapping: Record<UserState, Ceremony> = {
	[UserState.HP_MY_GLOBUS_BANNER_LOGIN]: Ceremony.SIGN_IN,
	[UserState.HP_MY_GLOBUS_BANNER_REGISTRATION]: Ceremony.SIGN_IN,
	[UserState.USER_MENU_ANONYMOUS_SLIDEOUT_LOGIN]: Ceremony.SIGN_IN,
	[UserState.USER_MENU_ANONYMOUS_SLIDEOUT_REGISTRATION]: Ceremony.SIGN_IN,
	[UserState.ONLINE_ORDER_BUTCHERY]: Ceremony.SIGN_IN,
	[UserState.ONLINE_ORDER_RESTAURANT]: Ceremony.SIGN_IN,
	[UserState.PRODUCT_TILE_LOGIN]: Ceremony.SIGN_IN,
	[UserState.PRODUCT_DETAIL_SHOPPING_LIST]: Ceremony.SIGN_IN,
	[UserState.PRODUCT_DETAIL_MY_GLOBUS_BANNER]: Ceremony.SIGN_IN,
	[UserState.USER_MENU_SHOPPING_LISTS]: Ceremony.SIGN_IN,
	[UserState.MY_GLOBUS_INTRODUCTORY_BANNER]: Ceremony.SIGN_IN,
	[UserState.USER_RECEIPTS_PREVIEW_LIST]: Ceremony.SIGN_IN,
	[UserState.MY_GLOBUS_REGISTRATION]: Ceremony.SIGN_IN,
	[UserState.LOGIN_PAGE]: Ceremony.SIGN_IN,
	[UserState.FORGOTTEN_PASSWORD_PAGE]: Ceremony.SIGN_IN,
	[UserState.FORGOTTEN_PASSWORD_PAGE_LOGIN]: Ceremony.SIGN_IN,
	[UserState.UPDATE_PROFILE]: Ceremony.UPDATE_PROFILE,
	[UserState.UPDATE_PROFILE_PAGE_PROFILE]: Ceremony.UPDATE_PROFILE,
	[UserState.UPDATE_PROFILE_PAGE_CHANGE_EMAIL]: Ceremony.UPDATE_PROFILE,
	[UserState.UPDATE_PROFILE_PAGE_PERSONAL_INFO]: Ceremony.UPDATE_PROFILE,
	[UserState.UPDATE_PROFILE_PAGE_CONTACT_INFO]: Ceremony.UPDATE_PROFILE,
	[UserState.UPDATE_PROFILE_PAGE_PREFERENCE]: Ceremony.UPDATE_PROFILE,
	[UserState.UPDATE_PROFILE_PAGE_PREFERENCE_ALERTS]: Ceremony.UPDATE_PROFILE,
	[UserState.UPDATE_PROFILE_PAGE_INTERESTS]: Ceremony.UPDATE_PROFILE,
	[UserState.UPDATE_PROFILE_PAGE_CONSENTS]: Ceremony.UPDATE_PROFILE,
	[UserState.UPDATE_PROFILE_RECEIPTS]: Ceremony.UPDATE_PROFILE,
	[UserState.LOGIN_PAGE_PROFILE]: Ceremony.SIGN_IN,
	[UserState.LOGIN_PAGE_CHANGE_EMAIL]: Ceremony.SIGN_IN,
	[UserState.LOGIN_PAGE_PERSONAL_INFO]: Ceremony.SIGN_IN,
	[UserState.LOGIN_PAGE_CONTACT_INFO]: Ceremony.SIGN_IN,
	[UserState.LOGIN_PAGE_PREFERENCE]: Ceremony.SIGN_IN,
	[UserState.LOGIN_PAGE_INTERESTS]: Ceremony.SIGN_IN,
	[UserState.LOGIN_PAGE_CONSENTS]: Ceremony.SIGN_IN,
	[UserState.REGISTRATION_PAGE]: Ceremony.SIGN_IN,
	[UserState.REGISTRATION_PAGE_LOGIN]: Ceremony.SIGN_IN,
	[UserState.LOGOUT]: Ceremony.LOGOUT,
	[UserState.LOGIN_MY_ACCOUNT]: Ceremony.SIGN_IN,
	[UserState.LOGIN_COUPONS]: Ceremony.SIGN_IN,
	[UserState.LOGIN_COUPON_DETAIL]: Ceremony.SIGN_IN,
	[UserState.LOGIN_GIFT_DETAIL]: Ceremony.SIGN_IN,
	[UserState.LOGIN_ACCOUNT_SETTINGS]: Ceremony.SIGN_IN,
	[UserState.LOGIN_CASH_BONUS]: Ceremony.SIGN_IN,
	[UserState.LOGIN_RECEIPTS]: Ceremony.SIGN_IN,
	[UserState.SHOPPING_LISTS]: Ceremony.SIGN_IN,
};

const stateToCrmScopeMapping: Partial<Record<UserState, CrmScope[]>> = {
	[UserState.HP_MY_GLOBUS_BANNER_LOGIN]: [CrmScope.WEB_STANDARD_LOGIN],
	[UserState.HP_MY_GLOBUS_BANNER_REGISTRATION]: [CrmScope.WEB_REGISTER],
	[UserState.USER_MENU_ANONYMOUS_SLIDEOUT_LOGIN]: [CrmScope.WEB_STANDARD_LOGIN],
	[UserState.USER_MENU_ANONYMOUS_SLIDEOUT_REGISTRATION]: [CrmScope.WEB_REGISTER],
	[UserState.ONLINE_ORDER_BUTCHERY]: [CrmScope.WEB_PRODUCTS_LOGIN],
	[UserState.ONLINE_ORDER_RESTAURANT]: [CrmScope.WEB_PRODUCTS_LOGIN],
	[UserState.PRODUCT_TILE_LOGIN]: [CrmScope.WEB_LIST_LOGIN],
	[UserState.PRODUCT_DETAIL_SHOPPING_LIST]: [CrmScope.WEB_LIST_LOGIN],
	[UserState.PRODUCT_DETAIL_MY_GLOBUS_BANNER]: [CrmScope.WEB_REGISTER],
	[UserState.USER_MENU_SHOPPING_LISTS]: [CrmScope.WEB_LIST_LOGIN],
	[UserState.MY_GLOBUS_INTRODUCTORY_BANNER]: [CrmScope.WEB_STANDARD_LOGIN],
	[UserState.USER_RECEIPTS_PREVIEW_LIST]: [CrmScope.WEB_STANDARD_LOGIN],
	[UserState.MY_GLOBUS_REGISTRATION]: [CrmScope.WEB_REGISTER],
	[UserState.LOGIN_PAGE]: [CrmScope.WEB_STANDARD_LOGIN],
	[UserState.FORGOTTEN_PASSWORD_PAGE]: [CrmScope.WEB_RESET_PASSWORD],
	[UserState.FORGOTTEN_PASSWORD_PAGE_LOGIN]: [CrmScope.WEB_LOGIN_AFTER_PASSWORD_RESET],
	[UserState.REGISTRATION_PAGE]: [CrmScope.WEB_REGISTER],
	[UserState.REGISTRATION_PAGE_LOGIN]: [CrmScope.WEB_STANDARD_LOGIN],
	[UserState.LOGIN_PAGE_PROFILE]: [CrmScope.WEB_STANDARD_LOGIN],
	[UserState.LOGIN_PAGE_CHANGE_EMAIL]: [CrmScope.WEB_STANDARD_LOGIN],
	[UserState.LOGIN_PAGE_PERSONAL_INFO]: [CrmScope.WEB_STANDARD_LOGIN],
	[UserState.LOGIN_PAGE_CONTACT_INFO]: [CrmScope.WEB_STANDARD_LOGIN],
	[UserState.LOGIN_PAGE_PREFERENCE]: [CrmScope.WEB_STANDARD_LOGIN],
	[UserState.LOGIN_PAGE_INTERESTS]: [CrmScope.WEB_STANDARD_LOGIN],
	[UserState.LOGIN_PAGE_CONSENTS]: [CrmScope.WEB_STANDARD_LOGIN],
	[UserState.LOGIN_MY_ACCOUNT]: [CrmScope.WEB_REPEAT_LOGIN],
	[UserState.LOGIN_COUPONS]: [CrmScope.WEB_REPEAT_LOGIN],
	[UserState.LOGIN_COUPON_DETAIL]: [CrmScope.WEB_REPEAT_LOGIN],
	[UserState.LOGIN_GIFT_DETAIL]: [CrmScope.WEB_REPEAT_LOGIN],
	[UserState.LOGIN_ACCOUNT_SETTINGS]: [CrmScope.WEB_REPEAT_LOGIN],
	[UserState.LOGIN_CASH_BONUS]: [CrmScope.WEB_REPEAT_LOGIN],
	[UserState.LOGIN_RECEIPTS]: [CrmScope.WEB_REPEAT_LOGIN],
	[UserState.SHOPPING_LISTS]: [CrmScope.WEB_STANDARD_LOGIN],
};

const REDIRECT_URI_OIDC = '/callbacks/login';

export default function useCrmRedirect() {
	const crmStateStore = useCrmStateStore();

	const ceremonyHandlers = {
		[Ceremony.SIGN_IN]: initAuthorization,
		[Ceremony.UPDATE_PROFILE]: initProfileEdit,
		[Ceremony.LOGOUT]: initLogout,
	};

	async function redirectToCrm(state: UserState, redirectOptions?: CrmStateRedirectOptions) {
		crmStateStore.setState(state, redirectOptions);

		const ceremony = stateToCeremonyMapping[state];
		const handler = ceremonyHandlers[ceremony];

		if (handler) {
			await handler(state);
		}
	}

	async function initAuthorization(userState: UserState) {
		const scopes = stateToCrmScopeMapping[userState] ?? [];
		const shouldIncludePrompt = userState === UserState.HP_MY_GLOBUS_BANNER_LOGIN
			|| userState === UserState.USER_MENU_ANONYMOUS_SLIDEOUT_LOGIN
			|| userState === UserState.ONLINE_ORDER_BUTCHERY
			|| userState === UserState.ONLINE_ORDER_RESTAURANT
			|| userState === UserState.PRODUCT_TILE_LOGIN
			|| userState === UserState.PRODUCT_DETAIL_SHOPPING_LIST
			|| userState === UserState.PRODUCT_DETAIL_MY_GLOBUS_BANNER
			|| userState === UserState.USER_MENU_SHOPPING_LISTS
			|| userState === UserState.MY_GLOBUS_INTRODUCTORY_BANNER
			|| userState === UserState.USER_RECEIPTS_PREVIEW_LIST;

		try {
			const response = await cancelTimeout(() => (OidcService.initAuthorize({
				redirectUrl: REDIRECT_URI_OIDC,
				scopes,
				...(shouldIncludePrompt && {prompt: 'login'}),
			})));
			return navigateTo(response.authorize_url, {external: true});
		} catch (e: any) {
			throw createError({
				message: 'Failed to init authorize',
				cause: e,
			});
		}
	}

	function initProfileEdit(userState: UserState) {
		const config = useRuntimeConfig();

		const stateToHashMapping: Partial<Record<UserState, string>> = {
			[UserState.UPDATE_PROFILE_PAGE_PERSONAL_INFO]: 'osobni-udaje',
			[UserState.UPDATE_PROFILE_PAGE_CONTACT_INFO]: 'kontaktni-udaje',
			[UserState.UPDATE_PROFILE_PAGE_PREFERENCE]: 'preference',
			[UserState.UPDATE_PROFILE_PAGE_INTERESTS]: 'zajmy',
			[UserState.UPDATE_PROFILE_PAGE_CONSENTS]: 'souhlasy',
			[UserState.UPDATE_PROFILE_RECEIPTS]: 'preference',
		};

		if (userState === UserState.UPDATE_PROFILE || userState === UserState.UPDATE_PROFILE_PAGE_PROFILE || userState === UserState.UPDATE_PROFILE_PAGE_CHANGE_EMAIL) {
			return navigateTo(`${config.public.sapUpdateProfileUrl}?site=web&return_url=${useBaseUrl()}/muj-ucet/`, {external: true});
		} else if (userState === UserState.UPDATE_PROFILE_PAGE_PREFERENCE_ALERTS) {
			return navigateTo(`${config.public.sapUpdateProfileUrl}?site=web&tab=alerts-tab-pane`, {external: true});
		} else {
			return navigateTo(`${config.public.sapUpdateProfileUrl}?site=web#${stateToHashMapping[userState]}`, {external: true});
		}
	}

	function initLogout() {
		const config = useRuntimeConfig();
		return navigateTo(`${config.public.sapLogoutUrl}?site=web&return_url=${useBaseUrl()}/`, {external: true});
	}

	return {redirectToCrm};
}
